import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, AlertCircle, AlertTriangle, Trash2, Database, Cloud, FileText, Radio, Upload } from 'lucide-react';
import exploreApi from './exploreApi';
import { getConnectorDefinition } from './connectors';
import { renderField } from './NewSource';

const ICON_MAP = { Database, Cloud, FileText, Radio, Upload };

const CATEGORY_CONFIG = {
  database: { icon: 'Database', label: 'Database', color: 'text-primary', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  saas: { icon: 'Cloud', label: 'SaaS Application', color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  file: { icon: 'FileText', label: 'File System', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
  streaming: { icon: 'Radio', label: 'Streaming', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
};

function SourceIcon({ category, className }) {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.database;
  const Icon = ICON_MAP[cfg.icon] || Database;
  return <Icon className={className} />;
}

function DeleteConfirmModal({ sourceName, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Delete Source</h2>
        </div>
        <p className="text-sm text-gray-600">
          Are you sure you want to delete <span className="font-semibold">"{sourceName}"</span>? This action cannot be undone.
        </p>
        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? <><Loader2 className="h-4 w-4 animate-spin" /> Deleting…</> : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EditSource() {
  const navigate = useNavigate();
  const location = useLocation();
  const sourceId = new URLSearchParams(location.search).get('id');

  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', config: {} });

  useEffect(() => {
    if (!sourceId) {
      setError('Source ID is required');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const data = await exploreApi.getSource(sourceId);
        setSource(data);
        setFormData({ name: data.name, config: data.config || {} });
      } catch (err) {
        setError(err.message || 'Failed to load source');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sourceId]);

  const handleConfigChange = (key, value) => {
    setFormData(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
    setTestResult(null);
  };

  const handleTest = async () => {
    if (!sourceId) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      await exploreApi.updateSource(sourceId, { name: formData.name, config: formData.config });
      const result = await exploreApi.testConnection(sourceId);
      setTestResult({ success: true, message: result?.message || 'Connection successful!' });
    } catch (err) {
      setTestResult({ success: false, message: err.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!sourceId) return;
    if (!testResult?.success) {
      setError('Please test the connection successfully before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await exploreApi.updateSource(sourceId, { name: formData.name, config: formData.config, status: 'connected' });
      navigate('/data-studio', { state: { tab: 'analyzeData' } });
    } catch (err) {
      setError(err.message || 'Failed to save source');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!sourceId) return;
    setDeleting(true);
    try {
      await exploreApi.deleteSource(sourceId);
      navigate('/data-studio', { state: { tab: 'analyzeData' } });
    } catch (err) {
      setError(err.message || 'Failed to delete source');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-500">Loading source…</p>
        </div>
      </div>
    );
  }

  if (error && !source) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20 p-6">
        <button
          onClick={() => navigate('/data-studio', { state: { tab: 'analyzeData' } })}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Analyze
        </button>
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg max-w-lg">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const connector = getConnectorDefinition(source?.connector || '');
  const category = connector?.category || 'database';
  const typeCfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.database;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20">
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/data-studio', { state: { tab: 'analyzeData' } })}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${typeCfg.bgColor} border ${typeCfg.borderColor}`}>
                <SourceIcon category={category} className={`h-5 w-5 ${typeCfg.color}`} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit {source?.connector?.toUpperCase()} Source</h1>
                <p className="text-sm text-gray-500 mt-0.5">Configure connection settings for your {typeCfg.label.toLowerCase()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Source name */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
            <p className="font-semibold text-gray-900">Source Details</p>
          </div>
          <div className="p-6 space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Source Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="My Data Source"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Connection config */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
            <p className="font-semibold text-gray-900">Connection Configuration</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {connector ? `${connector.name} connection settings` : `${source?.connector} connection settings`}
            </p>
          </div>
          <div className="p-6 space-y-5">
            {connector?.fields.length > 0 ? (
              connector.fields.map(field => renderField(field, formData.config[field.name] ?? '', handleConfigChange))
            ) : (
              /* Fallback: raw JSON editor for unknown connectors */
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">Configuration (JSON)</label>
                <textarea
                  value={JSON.stringify(formData.config, null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setFormData(prev => ({ ...prev, config: parsed }));
                    } catch {}
                  }}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`flex items-center gap-3 p-4 border rounded-lg ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {testResult.success
              ? <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
              : <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />}
            <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>{testResult.message}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleTest}
            disabled={testing || saving || deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {testing ? <><Loader2 className="h-4 w-4 animate-spin" /> Testing…</> : <><Database className="h-4 w-4" /> Test Connection</>}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !testResult?.success || deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primaryDark disabled:opacity-50 transition-colors"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Check className="h-4 w-4" /> Save &amp; Continue</>}
          </button>
        </div>

        {/* Delete */}
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={saving || testing || deleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Source
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          sourceName={source?.name}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
